/**
 * The browser side of Web Push (Clinical Bridge plan section 13.6): is it
 * possible here, is this an iPhone that must be installed first, and how to
 * register the service worker and subscribe with the deployment's public
 * key. Pure of React so it is testable and reusable.
 */

const SERVICE_WORKER_PATH = '/sw.js'
/** Where this browser remembers which contact is its own device, a per-viewer convenience only. */
const DEVICE_CONTACT_KEY = 'autonome.webPush.contactId'

export type PushSupport = 'supported' | 'install_first' | 'unsupported'

/** Whether this browser can take push: Safari on iPhone offers it only to an app added to the home screen. */
export function pushSupport(nav: Navigator = navigator, win: Window = window): PushSupport {
  const hasApis = 'serviceWorker' in nav && 'PushManager' in win && 'Notification' in win
  const isIosSafari = /iPhone|iPad|iPod/.test(nav.userAgent) && !/CriOS|FxiOS/.test(nav.userAgent)
  const standalone = ('standalone' in nav && (nav as Navigator & { standalone?: boolean }).standalone === true) || win.matchMedia?.('(display-mode: standalone)').matches === true
  if (isIosSafari && !standalone) {
    return hasApis ? 'supported' : 'install_first'
  }
  return hasApis ? 'supported' : 'unsupported'
}

/** The VAPID public key as the Push API wants it. */
export function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4)
  const normalised = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(normalised)
  return Uint8Array.from(raw, (char) => char.charCodeAt(0))
}

/** Registers the service worker, asks permission and subscribes; answers the subscription as the API stores it, or the reason it could not. */
export async function subscribeToPush(publicKey: string): Promise<{ ok: true; subscription: PushSubscriptionJSON } | { ok: false; reason: string }> {
  try {
    const registration = await navigator.serviceWorker.register(SERVICE_WORKER_PATH)
    const ready = await navigator.serviceWorker.ready
    const permission = await Notification.requestPermission()
    if (permission !== 'granted') {
      return { ok: false, reason: permission === 'denied' ? 'Notifications are blocked for this site in your browser settings.' : 'Notifications were not allowed.' }
    }
    const existing = await (ready ?? registration).pushManager.getSubscription()
    const subscription = existing ?? (await (ready ?? registration).pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource }))
    return { ok: true, subscription: subscription.toJSON() }
  } catch (error: unknown) {
    return { ok: false, reason: error instanceof Error && error.message ? error.message : 'This browser could not set up alerts.' }
  }
}

/** Drops this browser's own subscription so the device stops receiving even if the server still had it. */
export async function unsubscribeFromPush(): Promise<void> {
  try {
    const registration = await navigator.serviceWorker.getRegistration(SERVICE_WORKER_PATH)
    const subscription = await registration?.pushManager.getSubscription()
    await subscription?.unsubscribe()
  } catch {
    // Nothing to undo.
  }
}

export function rememberDeviceContact(contactId: string | null): void {
  try {
    if (contactId === null) {
      localStorage.removeItem(DEVICE_CONTACT_KEY)
    } else {
      localStorage.setItem(DEVICE_CONTACT_KEY, contactId)
    }
  } catch {
    // Storage may be unavailable; the card then just cannot tell which contact is this device.
  }
}

export function deviceContactId(): string | null {
  try {
    return localStorage.getItem(DEVICE_CONTACT_KEY)
  } catch {
    return null
  }
}

/** A short name for this browser, offered as the contact's label. */
export function describeDevice(nav: Navigator = navigator): string {
  const ua = nav.userAgent
  if (/iPhone/.test(ua)) return 'iPhone'
  if (/iPad/.test(ua)) return 'iPad'
  if (/Android/.test(ua)) return 'Android phone'
  if (/Macintosh/.test(ua)) return 'Mac'
  if (/Windows/.test(ua)) return 'Windows PC'
  return 'This device'
}
